/**
 * Google Sheet Script Installation Schemas
 *
 * API contracts for auto-installing Apps Script to spreadsheets.
 * Google Sheets Auto-Install
 */

import { z } from "zod";

/**
 * Install Script Request Body
 *
 * spreadsheetId can be:
 * - Full URL: https://docs.google.com/spreadsheets/d/{id}/edit
 * - Just the ID: 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms
 *
 * Backend will extract ID from URL if needed.
 */
export const InstallScriptBodySchema = z.object({
  spreadsheetId: z.string().min(1, "Spreadsheet ID or URL required"),
  oauthConnectionId: z.string().uuid("Valid OAuth connection ID required"),
  functionName: z
    .string()
    .min(1)
    .max(50)
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      "Must be a valid function name (letters, numbers, underscores)"
    )
});

export type InstallScriptBody = z.infer<typeof InstallScriptBodySchema>;

/**
 * Install Script Response
 *
 * Returns script installation details for UI feedback.
 */
export const InstallScriptResponseSchema = z.object({
  scriptId: z.string(),
  scriptUrl: z.string().url(),
  spreadsheetId: z.string(),
  spreadsheetUrl: z.string().url(),
  functionName: z.string(),
  installedAt: z.string().datetime()
});

export type InstallScriptResponse = z.infer<typeof InstallScriptResponseSchema>;

/**
 * Delete Script Request Body
 *
 * Removes script tracking from Nexus (deploymentSettings + DeploymentSession).
 * Google Apps Script API has no delete endpoint for container-bound scripts,
 * so the user must manually remove the script from Extensions > Apps Script.
 */
export const DeleteScriptBodySchema = z.object({
  spreadsheetId: z.string().min(1, "Spreadsheet ID required")
});

export type DeleteScriptBody = z.infer<typeof DeleteScriptBodySchema>;

/**
 * Delete Script Response
 */
export const DeleteScriptResponseSchema = z.object({
  deleted: z.boolean()
});

export type DeleteScriptResponse = z.infer<typeof DeleteScriptResponseSchema>;
